import { createI18n } from "vue-i18n";
import { getSupportedLanguage } from "@/shared-helpers/language.helper";

export const supportedLanguages = {
  data: {
    locales: [
      { locale: "en_US", name: "English" },
      { locale: "en_ZA", name: "English (South Africa)" },
      { locale: "es_CO", name: "Español Colombia" },
      { locale: "es_EC", name: "Español Ecuador" },
      { locale: "es_MX", name: "Español México" },
      { locale: "es_PA", name: "Español Panamá" },
      { locale: "es_PE", name: "Español Perú" },
      { locale: "pt_BR", name: "Português Brasil" },
      { locale: "id_ID", name: "Bahasa Indonesia" },
      { locale: "ceb_PH", name: "Cebuano" },
      { locale: "tl_PH", name: "Tagalog" },
    ],
  },
};

// const supportedLocales = supportedLanguages.data.locales.map(
//   (lang) => lang.locale
// );

// const validateLocale = (locale: string | null): string => {
//   if (!locale) return "es_MX";

//   if (supportedLocales.includes(locale)) {
//     return locale;
//   }

//   console.warn(
//     `Locale ${locale} is not supported. Falling back to default locale.`
//   );
//   return "es_MX";
// };

let defaultLanguage: string | null;

try {
  if (
    typeof window !== "undefined" &&
    typeof localStorage?.getItem === "function"
  ) {
    defaultLanguage = localStorage.getItem("lang")
      ? localStorage.getItem("lang")
      : getSupportedLanguage(window.navigator.language || "");
  } else if (typeof window !== "undefined") {
    defaultLanguage = getSupportedLanguage(window.navigator.language || "");
  } else {
    defaultLanguage = "es_MX";
  }
} catch (error) {
  console.warn("Error accessing localStorage for language preference:", error);
  defaultLanguage = "es_MX";
}

const currentLang = defaultLanguage || "es_MX";
// const currentLang = validateLocale(defaultLanguage);

export const fallbackLocales = {
  en: ["en_US"],
  es: ["es_MX"],
  pt: ["pt_BR", "es_MX"],
  en_MX: ["es_MX"],
  en_PH: ["en_US"],
  en_US: ["en_MX"],
  en_ZA: ["en_US"],
  es_CO: ["es_MX"],
  es_EC: ["es_MX"],
  es_MX: ["en_US"],
  es_PA: ["es_MX"],
  es_PE: ["es_MX"],
  es_BR: ["es_MX"],
  es_ZA: ["es_MX"],
  id_ID: ["en_US"],
  pt_BR: ["es_MX"],
  ceb_PH: ["en_US"],
  tl_PH: ["en_US"],
};

async function loadTranslations(locale: string) {
  try {
    const sharedTranslations = await import(`./shared/messages`).then(
      (m) => m.default[locale] || {},
    );

    return {
      SHARED: sharedTranslations,
    };
  } catch (error) {
    console.error(`Error loading translations for locale ${locale}:`, error);
    return {};
  }
}

// Load initial translations for current language and its fallbacks
async function loadInitialTranslations() {
  const locales = [currentLang];
  const fallbacks =
    fallbackLocales[currentLang as keyof typeof fallbackLocales] || [];

  // Add fallback locales
  fallbacks.forEach((fallback) => {
    if (!locales.includes(fallback)) {
      locales.push(fallback);
    }
  });

  const translations = {};

  for (const locale of locales) {
    translations[locale] = await loadTranslations(locale);
  }

  return translations;
}

export const supportedLanguagesMap = supportedLanguages.data.locales.reduce(
  (map, lang) => {
    map[lang.locale] = lang.name;
    return map;
  },
  {} as Record<string, string>,
);

const i18n = createI18n({
  locale: currentLang,
  fallbackLocale: fallbackLocales,
  legacy: false,
  missingWarn: false,
  messages: {},
});

export async function setI18nLanguage(locale: string): Promise<string> {
  if (!i18n.global.availableLocales.includes(locale)) {
    const messages = await loadTranslations(locale);
    i18n.global.setLocaleMessage(locale, messages);
  }

  i18n.global.locale.value = locale;

  if (typeof localStorage !== "undefined") {
    localStorage.setItem("lang", locale);
  }

  return locale;
}
//   const validatedLocale = validateLocale(locale);

//   if (!i18n.global.availableLocales.includes(validatedLocale)) {
//     const messages = await loadTranslations(validatedLocale);
//     i18n.global.setLocaleMessage(validatedLocale, messages);
//   }

//   i18n.global.locale.value = validatedLocale;

//   if (typeof localStorage !== "undefined") {
//     localStorage.setItem("lang", validatedLocale);
//   }

//   return validatedLocale;
// }

loadInitialTranslations().then((translations) => {
  Object.keys(translations).forEach((localeKey) => {
    const existing = i18n.global.getLocaleMessage?.(localeKey) || {};
    i18n.global.setLocaleMessage(localeKey, {
      ...existing,
      ...translations[localeKey],
    });
  });
});

export const CURRENT_LANG = currentLang;
export { useI18n } from "vue-i18n";
export default i18n;
